import { Link } from 'react-router-dom';
import type { Campaign } from '@/mocks/campaigns';
import useScrollAnimation from '@/hooks/useScrollAnimation';
import SAR from './SAR';

interface CampaignCardProps {
  campaign: Campaign;
  size?: 'default' | 'large';
  index?: number;
}

const CampaignCard = ({ campaign, size = 'default', index = 0 }: CampaignCardProps) => {
  const progress = Math.min((campaign.raisedAmount / campaign.goalAmount) * 100, 100);
  const { ref, visible } = useScrollAnimation({ threshold: 0.08 });

  const delay = (index % 3) * 80;

  return (
    <div
      ref={ref}
      style={{
        transitionDelay: `${delay}ms`,
        opacity: visible ? 1 : 0,
        transform: visible ? 'translateY(0px)' : 'translateY(28px)',
        transition: 'opacity 0.5s ease, transform 0.5s ease',
      }}
    >
      <Link
        to={`/campaign/${campaign.id}`}
        className="group block bg-white rounded-2xl overflow-hidden border border-stone-100 hover:border-emerald-200 transition-all duration-300 cursor-pointer"
        style={{ boxShadow: '0 4px 20px rgba(0,0,0,0.08), 0 1px 4px rgba(0,0,0,0.04)' }}
      >
        {/* Image */}
        <div className={`relative overflow-hidden ${size === 'large' ? 'h-56' : 'h-44'}`}>
          <img
            src={campaign.coverImage}
            alt={campaign.title}
            className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
          />
          {/* Badges */}
          <div className="absolute top-3 right-3 flex flex-col gap-1.5">
            {campaign.isLicensed && (
              <span className="bg-emerald-600 text-white text-xs px-2.5 py-1 rounded-full font-medium flex items-center gap-1">
                <i className="ri-shield-check-line text-xs"></i>
                مرخصة
              </span>
            )}
            {campaign.isVerified && (
              <span className="bg-white/90 text-stone-700 text-xs px-2.5 py-1 rounded-full font-medium flex items-center gap-1">
                <i className="ri-verified-badge-line text-emerald-600 text-xs"></i>
                موثقة
              </span>
            )}
          </div>
          {/* Type Badge */}
          <div className="absolute top-3 left-3">
            <span className={`text-xs px-2.5 py-1 rounded-full font-medium ${
              campaign.type === 'charity'
                ? 'bg-amber-500/90 text-white'
                : 'bg-stone-800/80 text-white'
            }`}>
              {campaign.type === 'charity' ? 'تبرع خيري' : 'دعم شخصي'}
            </span>
          </div>
          {/* Days Left */}
          <div className="absolute bottom-3 left-3">
            <span className="bg-black/60 text-white text-xs px-2.5 py-1 rounded-full">
              {campaign.daysLeft} يوم متبقي
            </span>
          </div>
        </div>

        {/* Content */}
        <div className="p-4" dir="rtl">
          <span className="text-xs text-emerald-600 font-medium">{campaign.category}</span>

          <h3 className="font-bold text-stone-800 mt-1 mb-2 text-sm leading-snug line-clamp-2 group-hover:text-emerald-700 transition-colors">
            {campaign.title}
          </h3>

          <div className="flex items-center gap-2 mb-3">
            <img
              src={campaign.creatorAvatar}
              alt={campaign.creatorName}
              className="w-6 h-6 rounded-full object-cover object-top"
            />
            <span className="text-xs text-stone-500">{campaign.creatorName}</span>
            <span className="text-stone-300 text-xs">•</span>
            <span className="text-xs text-stone-400 flex items-center gap-1">
              <i className="ri-map-pin-line text-xs"></i>
              {campaign.location}
            </span>
          </div>

          {/* Progress */}
          <div className="mb-3">
            <div className="h-1.5 bg-stone-100 rounded-full overflow-hidden">
              <div
                className="h-full bg-emerald-500 rounded-full transition-all duration-700"
                style={{ width: visible ? `${progress}%` : '0%' }}
              />
            </div>
            <div className="flex items-center justify-between mt-1.5">
              <span className="text-xs text-stone-500">{Math.round(progress)}% مكتمل</span>
              <span className="text-xs font-semibold text-stone-700"><SAR amount={campaign.raisedAmount} iconSize={11} /></span>
            </div>
          </div>

          {/* Stats */}
          <div className="flex items-center justify-between pt-3 border-t border-stone-50">
            <div className="flex items-center gap-1 text-stone-500">
              <i className="ri-user-heart-line text-xs"></i>
              <span className="text-xs">{campaign.donorsCount.toLocaleString('en-US')} داعم</span>
            </div>
            <div className="text-xs text-stone-400">
              الهدف: <SAR amount={campaign.goalAmount} iconSize={11} />
            </div>
          </div>

          {/* Endorsement badge */}
          {campaign.endorsements && campaign.endorsements.length > 0 && (
            <div className="mt-3 pt-3 border-t border-stone-50 flex items-center gap-2">
              <div className="flex -space-x-1.5 rtl:space-x-reverse">
                {campaign.endorsements.slice(0, 2).map((e) => (
                  <img
                    key={e.id}
                    src={e.avatar}
                    alt={e.name}
                    className="w-5 h-5 rounded-full object-cover border border-white"
                  />
                ))}
              </div>
              <span className="text-xs text-amber-700 font-medium flex items-center gap-1">
                <i className="ri-medal-line text-xs text-amber-500"></i>
                مُزكّاة من {campaign.endorsements.length} خبير
              </span>
            </div>
          )}
        </div>
      </Link>
    </div>
  );
};

export default CampaignCard;
